import {
  type SignInFinishResponse,
  SignInFinishResponseSchema,
  type SignInProvider,
  SignInProvidersResponseSchema,
  SignInStartResponseSchema,
} from '@shefira/shared';

import { isNativeApp } from '../platform/native.js';
import { call } from './account.js';
import { AuthError } from './session.js';

/**
 * Google and Steam sign-in (ADR 0054). The page leaves for the provider and
 * comes back to the client's origin with a `state` the server signed; on
 * boot, `consumeSignInReturn` hands the query string to the server and wipes
 * it from the address bar, so a reload or a shared link never replays it.
 */

export const PROVIDER_NAMES: Record<SignInProvider, string> = { google: 'Google', steam: 'Steam' };

/** The providers this server offers; none when it cannot be reached (the welcome screen still works). */
export async function signInProviders(): Promise<SignInProvider[]> {
  // Google refuses its sign-in inside an embedded web view, and both providers
  // come back to the web client's origin, not to the app (ADR 0075).
  if (isNativeApp()) return [];
  try {
    return (await call('/auth/providers', SignInProvidersResponseSchema, { method: 'GET' }))
      .providers;
  } catch {
    return [];
  }
}

/**
 * Leave for the provider. With a token, to link it to that account; the page
 * comes back to the game, where the account tab says how it went.
 */
export async function leaveForProvider(provider: SignInProvider, linkWith?: string): Promise<void> {
  const { url } = await call('/auth/oauth/start', SignInStartResponseSchema, {
    method: 'POST',
    body: { provider, link: linkWith !== undefined },
    ...(linkWith !== undefined ? { token: linkWith } : {}),
  });
  window.location.assign(url);
}

export type SignInReturn =
  | { kind: 'none' }
  | { kind: 'done'; result: SignInFinishResponse }
  | { kind: 'failed'; message: string; linking: boolean };

/**
 * Finish a sign-in the page came back from, if it did. `token` is any
 * session of the account a link was started from; the server checks it.
 */
export async function consumeSignInReturn(token: string | undefined): Promise<SignInReturn> {
  const query = new URLSearchParams(window.location.search);
  const state = query.get('state');
  // Google comes back with `code` or `error`, Steam with `openid.*`: anything else is not ours.
  const ours =
    state !== null && (query.has('code') || query.has('error') || query.has('openid.mode'));
  if (!ours) return { kind: 'none' };
  const params = Object.fromEntries(query.entries());
  window.history.replaceState(null, '', window.location.pathname);
  if (params.error !== undefined) {
    return { kind: 'failed', message: 'Connexion annulée.', linking: token !== undefined };
  }
  try {
    const result = await call('/auth/oauth/finish', SignInFinishResponseSchema, {
      method: 'POST',
      body: { params },
      ...(token !== undefined ? { token } : {}),
    });
    return { kind: 'done', result };
  } catch (cause: unknown) {
    return {
      kind: 'failed',
      message: cause instanceof AuthError ? cause.message : 'Connexion impossible.',
      linking: token !== undefined,
    };
  }
}

/** What the account tab says once the game is up, after a link came back. */
let linkNotice: { text: string; tone: 'ok' | 'refused' } | null = null;

export function setLinkNotice(notice: { text: string; tone: 'ok' | 'refused' }): void {
  linkNotice = notice;
}

/** The notice, once: read, it is gone. */
export function takeLinkNotice(): { text: string; tone: 'ok' | 'refused' } | null {
  const notice = linkNotice;
  linkNotice = null;
  return notice;
}

/** Whether a link came back and the account tab should open to say so. */
export function hasLinkNotice(): boolean {
  return linkNotice !== null;
}
